import { Upload } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import { useState } from 'react';
import { AxiosProgressEvent } from 'axios';
import { useUploadImportFile } from '../../../../serverState/imports';

const { Dragger } = Upload;

const ACCEPTED_FILE_TYPES = '.csv,.xls,.xlsx';
const MAX_FILE_SIZE_MB = 10;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

interface ImportUploadAreaProps {
  workspaceId: number;
  onUploadSuccess: (importId: number) => void;
}

export const ImportUploadArea = ({ workspaceId, onUploadSuccess }: ImportUploadAreaProps) => {
  const [uploadPhase, setUploadPhase] = useState<'idle' | 'uploading' | 'parsing'>('idle');
  const [uploadPercent, setUploadPercent] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const uploadMutation = useUploadImportFile();

  const handleUpload = (file: File) => {
    if (file.size > MAX_FILE_SIZE_BYTES) {
      setError(`File size exceeds ${MAX_FILE_SIZE_MB}MB limit`);
      return false;
    }

    const extension = file.name.split('.').pop()?.toLowerCase();
    if (!extension || !['csv', 'xls', 'xlsx'].includes(extension)) {
      setError('Only CSV, XLS, and XLSX files are supported');
      return false;
    }

    setError(null);
    setUploadPhase('uploading');
    setUploadPercent(0);

    uploadMutation.mutate(
      {
        workspaceId,
        file,
        onUploadProgress: (progressEvent: AxiosProgressEvent) => {
          const percent = Math.round((progressEvent.loaded * 100) / (progressEvent.total ?? progressEvent.loaded));
          setUploadPercent(percent);
          if (percent >= 100) {
            setUploadPhase('parsing');
          }
        },
      },
      {
        onSuccess: (data) => {
          setUploadPhase('idle');
          onUploadSuccess(data.id);
        },
        onError: (err) => {
          setUploadPhase('idle');
          const errorMessage = (err as any)?.response?.data?.message || 'Upload failed. Please try again.';
          setError(errorMessage);
        },
      }
    );

    return false;
  };

  if (uploadPhase !== 'idle') {
    return (
      <div className="w-full h-[200px] rounded-lg border border-dashed border-neutral-500 flex flex-col items-center justify-center gap-3 bg-neutral-100">
        {uploadPhase === 'uploading' ? (
          <>
            <div className="text-neutral-900 text-sm font-medium">Uploading...</div>
            <div className="w-[200px] h-2 bg-neutral-300 rounded-full overflow-hidden">
              <div
                className="h-full bg-taskonioGreen-600 rounded-full transition-all duration-300"
                style={{ width: `${uploadPercent}%` }}
              />
            </div>
            <div className="text-neutral-700 text-xs">{uploadPercent}%</div>
          </>
        ) : (
          <>
            <div className="text-neutral-900 text-sm font-medium">Parsing file...</div>
            <div className="w-[200px] h-2 bg-neutral-300 rounded-full overflow-hidden">
              <div className="h-full bg-taskonioGreen-600 rounded-full animate-pulse w-full" />
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="w-full">
      <Dragger
        accept={ACCEPTED_FILE_TYPES}
        showUploadList={false}
        beforeUpload={handleUpload}
        multiple={false}
        className="import-upload-dragger"
      >
        <div className="flex flex-col items-center justify-center gap-2 py-4">
          <InboxOutlined className="text-3xl text-neutral-700" />
          <div className="text-neutral-900 text-sm font-medium">
            Drag and drop your file here
          </div>
          <div className="text-neutral-700 text-xs">
            or click to choose a file (CSV, XLS, XLSX - max {MAX_FILE_SIZE_MB}MB)
          </div>
        </div>
      </Dragger>
      {error && (
        <div className="mt-2 flex items-center justify-between">
          <div className="text-red-600 text-xs">{error}</div>
          <button
            className="text-xs text-taskonioGreen-600 hover:underline cursor-pointer bg-transparent border-none"
            onClick={() => setError(null)}
          >
            Dismiss
          </button>
        </div>
      )}
    </div>
  );
};
